import { AGENT_URL } from '../../config/env'
import { chameleon } from '@/lib/chameleon'
import type { DocumentRecord, DocumentPayload } from './types'

export async function getDocuments(token: string, qs: URLSearchParams) {
  const response = await fetch(`${AGENT_URL}/data/documents?${qs.toString()}`, {
    headers: {
      Authorization: token,
    },
  })

  if (!response.ok) {
    throw new Error(`Failed to fetch documents: ${response.status}`)
  }

  return response.json()
}

export async function getDocument(
  documentId: string,
  token: string
): Promise<DocumentRecord> {
  const response = await fetch(`${AGENT_URL}/docs/${documentId}`, {
    headers: {
      Authorization: token,
    },
  })

  if (!response.ok) {
    throw new Error(`Failed to fetch document: ${response.status}`)
  }

  const result = await response.json()
  return result.document
}

export async function createDocument(
  agentId: string,
  payload: DocumentPayload,
  token: string
): Promise<DocumentRecord> {
  const response = await fetch(`${AGENT_URL}/${agentId}/docs`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: token,
    },
    body: JSON.stringify(payload),
  })

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}))
    throw new Error(
      errorData.error || `Failed to create document: ${response.status}`
    )
  }

  const result = await response.json()
  if (!result.document) throw new Error('Create returned no document')
  // Tracked here rather than in the modal: the agent's "New Document" and "Save
  // response" buttons create documents through this same function.
  chameleon.track('Document Created')
  return result.document
}

export async function updateDocument(
  documentId: string,
  // Partial: the backend updates only the keys present, so callers can send
  // content-only (`{ data }`) or metadata-only patches.
  payload: Partial<DocumentPayload>,
  token: string
): Promise<DocumentRecord> {
  const response = await fetch(`${AGENT_URL}/docs/${documentId}`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      Authorization: token,
    },
    body: JSON.stringify(payload),
  })

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}))
    throw new Error(
      errorData.error || `Failed to update document: ${response.status}`
    )
  }

  const result = await response.json()
  if (!result.document) throw new Error('Update returned no document')
  return result.document
}

// Archive/restore is just an update with the `archived` key; the backend
// derives archived_at/archived_by from the authenticated user.
export function archiveDocument(
  documentId: string,
  archived: boolean,
  token: string
): Promise<DocumentRecord> {
  return updateDocument(documentId, { archived }, token)
}

export async function generateDocumentMetadata(
  content: string,
  token: string
): Promise<{ title: string; description: string }> {
  const response = await fetch(`${AGENT_URL}/docs/generate-metadata`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: token,
    },
    body: JSON.stringify({ content }),
  })

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}))
    throw new Error(
      errorData.error || `Failed to generate metadata: ${response.status}`
    )
  }

  return await response.json()
}
